import Dialog from "@rio-cloud/rio-uikit/Dialog";
import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import { Notification, Select } from "@rio-cloud/rio-uikit";
import DailyFleetExpenseChart, { DailyExpenseSeriesItem } from "@components/Tolls/DailyFleetExpenseChart";
import TopConcessionariasChart, { ConcessionariaExpenseItem } from "@components/Tolls/TopConcessionariasChart";
import TopTollPlazasCard, { TopTollPlazaItem } from "@components/Tolls/TopTollPlazasCard";
import TollConcentrationMapCard from "@components/Tolls/TollConcentrationMapCard";
import TollsTable, { TollRecord } from "@components/Tolls/TollsTable";
import SelectedFilters from "@components/SelectedFilters";
import KpiCardsSkeleton from "@components/Skeletons/KpiCardsSkeleton";
import DailyFleetExpenseChartSkeleton from "@components/Skeletons/DailyFleetExpenseChartSkeleton";
import TopTollPlazasCardSkeleton from "@components/Skeletons/TopTollPlazasCardSkeleton";
import TopConcessionariasChartSkeleton from "@components/Skeletons/TopConcessionariasChartSkeleton";
import DetailFormSkeleton from "@components/Skeletons/DetailFormSkeleton";
import { MonthNavigation } from "@components/Dashboard";
import { useT } from "@hooks";
import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useIntl } from "react-intl";
import {
  getTollsSummary,
  getTollsDailyExpenses,
  getTopTollLocations,
  getTopTollConcessionaires,
  getTolls,
  getTollMapPins,
  getTollDetail,
  downloadTollsXlsx,
  TollsSummary,
  TollMapPin,
  TollDetail,
  EMPTY_TOLLS_SUMMARY,
} from "@services/tolls";
import { setEndDate, setStartDate, useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import type { TableExportLayout } from "@utils/tableExport";
import { useAppDispatch } from "@store";
import { useIsTreeReady } from "@store/tmsAssetTree/tmsAssetTreeSlice";
import { useStoredFilterValues, useRequestFilters } from "@hooks/useStoredFilters";
import { useMonthNavigation } from "@hooks/useMonthNavigation";
import KpiCard, { KpiCardDelta, KpiCardIcon } from "@components/Dashboard/KPICards/KpiCard";
import KpiInfoTooltip, { formatPeriodRange } from "@components/shared/KpiInfoTooltip";
import KpiComparisonTooltip from "@components/shared/KpiComparisonTooltip";
import { toKpiComparison } from "@utils/kpiComparison";
import type { KpiComparison } from "@types";

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatInteger = (value: number) => value.toLocaleString("pt-BR");

const formatComparisonCurrency = (value: number) => `R$ ${formatCurrency(value)}`;

const formatAverageFormula = (total: number, divisor: number, average: number) =>
  divisor > 0
    ? `R$ ${formatCurrency(total)} ÷ ${formatInteger(divisor)} = R$ ${formatCurrency(average)}`
    : `R$ ${formatCurrency(average)}`;

interface TollKpiCardProps {
  label: string;
  tooltip?: React.ReactNode;
  value: string;
  icon: React.ReactNode;
  deltaPercentage: number;
  comparison?: KpiComparison | null;
  formatComparisonValue: (value: number) => string;
}

const TollKpiCard = ({
  label,
  tooltip,
  value,
  icon,
  deltaPercentage,
  comparison,
  formatComparisonValue,
}: TollKpiCardProps) => {
  const t = useT();
  return (
    <KpiCard
      label={
        <>
          {label}
          {tooltip}
        </>
      }
      value={value}
      icon={icon}
      contentGapClassName="gap-20"
      footer={
        <KpiComparisonTooltip comparison={comparison} formatValue={formatComparisonValue}>
          <KpiCardDelta
            deltaPercentage={comparison ? comparison.variationPercent : deltaPercentage}
            vsLabel={t("kpi.vsLastMonth")}
          />
        </KpiComparisonTooltip>
      }
    />
  );
};

const CurrencyIcon = () => <KpiCardIcon />;

const parseAxles = (text: string): number => {
  const match = /\d+/.exec(text);
  return match ? Number(match[0]) : 0;
};

const resolveMomentLocale = (locale: string): string => {
  if (locale.startsWith("en")) return "en-gb";
  if (locale.startsWith("es")) return "es";
  return "pt-br";
};

interface TollDetailFormProps {
  detail: TollDetail;
  momentLocale: string;
}

const TollDetailForm = ({ detail, momentLocale }: Readonly<TollDetailFormProps>) => {
  const t = useT();
  const noop = () => {};
  const dateValue = detail.dateTime ? moment(detail.dateTime).format("DD/MM/YYYY") : "";
  const timeValue = detail.dateTime ? moment(detail.dateTime).format("HH:mm") : "-";
  const amountValue = detail.amount.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="padding-x-20">
      <form>
        <fieldset>
          <div className="display-flex flex-wrap gap-15">
            <div className="flex-1-1 form-group width-100pct-xs">
              <label htmlFor="tollDate">{t("toll.form.label.date")}</label>
              <DatePicker
                id="tollDate"
                dateFormat="DD/MM/YYYY"
                locale={momentLocale}
                onChange={noop}
                value={dateValue}
                className="margin-0"
                timeFormat={false}
                inputProps={{ readOnly: true, disabled: true }}
              />
            </div>
            <div className="flex-1-1 form-group width-100pct-xs">
              <label htmlFor="tollTime">{t("toll.form.label.time")}</label>
              <div className="input-group">
                <span className="input-group-addon">
                  <span className="rioglyph rioglyph-time" />
                </span>
                <input id="tollTime" type="text" className="form-control" readOnly value={timeValue} />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="margin-0 margin-bottom-5" htmlFor="tollVehicle">
              {t("toll.form.label.vehicle")}
            </label>
            <Select
              id="tollVehicle"
              disabled
              options={[{ id: "detail-toll-vehicle", label: detail.vehicle || "-" }]}
              value={["detail-toll-vehicle"]}
              onChange={noop}
              placeholder="Selecione"
              className="rounded-small flex-1-1"
            />
          </div>

          <div className="form-group">
            <label className="margin-0 margin-bottom-5" htmlFor="tollAxes">
              {t("toll.form.label.axesCount")}
            </label>
            <Select
              id="tollAxes"
              disabled
              options={[{ id: "detail-toll-axes", label: detail.numberOfAxes || "-" }]}
              value={["detail-toll-axes"]}
              onChange={noop}
              placeholder="Selecione"
              className="rounded-small flex-1-1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="tollPlaza">{t("toll.form.label.plaza")}</label>
            <input id="tollPlaza" type="text" className="form-control" readOnly value={detail.plaza || "-"} />
          </div>

          <div className="form-group">
            <label htmlFor="tollCityState">{t("toll.form.label.cityState")}</label>
            <input id="tollCityState" type="text" className="form-control" readOnly value={detail.cityState || "-"} />
          </div>

          <div className="form-group">
            <label htmlFor="tollConcessionaire">{t("toll.form.label.concessionaire")}</label>
            <input
              id="tollConcessionaire"
              type="text"
              className="form-control"
              readOnly
              value={detail.concessionaire || "-"}
            />
          </div>

          <div className="form-group">
            <label htmlFor="tollAmount">{t("toll.form.label.value")}</label>
            <div className="input-group">
              <span className="input-group-addon">R$</span>
              <input id="tollAmount" type="text" className="form-control" readOnly value={amountValue} />
            </div>
          </div>

          <div className="form-group">
            <label className="margin-0 margin-bottom-5" htmlFor="tollPayment">
              {t("toll.form.label.paymentMethod")}
            </label>
            <Select
              id="tollPayment"
              disabled
              options={[{ id: "detail-toll-payment", label: detail.payment || "-" }]}
              value={["detail-toll-payment"]}
              onChange={noop}
              placeholder="Selecione"
            />
          </div>
        </fieldset>
      </form>
    </div>
  );
};

const TollsScreen = () => {
  const t = useT();
  const { locale } = useIntl();
  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);
  const dispatch = useAppDispatch();

  const startDate = useGetStoredStartDate();
  const endDate = useGetStoredEndDate();
  const kpiPeriodRange = formatPeriodRange(startDate, endDate);
  const isTreeReady = useIsTreeReady();
  const storedFilters = useStoredFilterValues();

  const { showDateRangePicker, setShowDateRangePicker, currentMonth, goToPrevMonth, goToNextMonth } =
    useMonthNavigation({ startDate, endDate, momentLocale, dispatch });

  const requestFilters = useRequestFilters(storedFilters, startDate, endDate);

  const [summary, setSummary] = useState<TollsSummary>(EMPTY_TOLLS_SUMMARY);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [dailyExpenseData, setDailyExpenseData] = useState<DailyExpenseSeriesItem[]>([]);
  const [isLoadingDailyExpenses, setIsLoadingDailyExpenses] = useState(true);
  const [topTollPlazas, setTopTollPlazas] = useState<TopTollPlazaItem[]>([]);
  const [isLoadingTopTollPlazas, setIsLoadingTopTollPlazas] = useState(true);
  const [topConcessionaires, setTopConcessionaires] = useState<ConcessionariaExpenseItem[]>([]);
  const [isLoadingTopConcessionaires, setIsLoadingTopConcessionaires] = useState(true);
  const [tolls, setTolls] = useState<TollRecord[]>([]);
  const [tollsPage, setTollsPage] = useState(0);
  const [tollsTotalRecords, setTollsTotalRecords] = useState(0);
  const [isLoadingTable, setIsLoadingTable] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [tollsRefreshKey, setTollsRefreshKey] = useState(0);
  const [mapPins, setMapPins] = useState<TollMapPin[]>([]);
  const [detailModal, setDetailModal] = useState<{ open: boolean; data: TollDetail | null; loading: boolean }>({
    open: false,
    data: null,
    loading: false,
  });

  const TOLLS_PAGE_SIZE = 20;

  useEffect(() => {
    let cancelled = false;
    if (!startDate || !endDate) return;
    if (!isTreeReady) return;
    setIsLoadingSummary(true);
    setIsLoadingDailyExpenses(true);
    setIsLoadingTopTollPlazas(true);
    setIsLoadingTopConcessionaires(true);
    getTollsSummary(requestFilters)
      .then((result) => {
        if (!cancelled) setSummary(result);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSummary(false);
      });
    getTollsDailyExpenses(requestFilters)
      .then((items) => {
        if (cancelled) return;
        setDailyExpenseData(
          items.map((item) => ({
            date: item.date,
            current: item.amount,
            previous: item.previousAmount,
          }))
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDailyExpenses(false);
      });
    getTopTollLocations(requestFilters, 5, false)
      .then((items) => {
        if (cancelled) return;
        setTopTollPlazas(
          items.map((item, index) => ({
            rank: index + 1,
            name: item.location,
            concessionaria: item.concessionaire,
            count: item.passages,
          }))
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTopTollPlazas(false);
      });
    getTopTollConcessionaires(requestFilters, 10, false)
      .then((items) => {
        if (cancelled) return;
        setTopConcessionaires(
          items.map((item) => ({
            concessionaria: item.concessionaire,
            expense: item.totalAmount,
          }))
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTopConcessionaires(false);
      });
    getTollMapPins(requestFilters).then((items) => {
      if (!cancelled) setMapPins(items);
    });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, requestFilters, isTreeReady]);

  const totalExpense = summary.totalSpent.value;
  const totalExpenseDelta = summary.totalSpent.variationPercent;
  const totalPasses = summary.totalPassages.value;
  const totalPassesDelta = summary.totalPassages.variationPercent;
  const totalVehicles = summary.totalVehicles.value;
  const averageCostPerPass = summary.averageCostPerPassage.value;
  const averageCostPerPassDelta = summary.averageCostPerPassage.variationPercent;
  const averageCostPerVehicle = summary.averageCostPerVehicle.value;
  const averageCostPerVehicleDelta = summary.averageCostPerVehicle.variationPercent;

  const mapTollRow = useCallback(
    (item: Awaited<ReturnType<typeof getTolls>>["content"][number]): TollRecord => ({
      id: item.transactionId,
      dateTime: item.dateTime ? moment(item.dateTime).format("DD/MM/YYYY - HH:mm") : "-",
      vehicle: item.vehicle,
      axles: parseAxles(item.numberOfAxes),
      plaza: item.plaza,
      cityState: item.cityState,
      concessionaria: item.concessionaire,
      amount: item.amount,
      paymentMethod: item.payment,
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    if (!startDate || !endDate) return;
    if (!isTreeReady) return;
    setTollsPage(0);
    setTolls([]);
    setTollsTotalRecords(0);
    setIsLoadingTable(true);
    getTolls(requestFilters, 0, TOLLS_PAGE_SIZE)
      .then((result) => {
        if (cancelled) return;
        setTolls(result.content.map(mapTollRow));
        setTollsTotalRecords(result.totalElements ?? result.content.length);
        setTollsPage(0);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, tollsRefreshKey, requestFilters, isTreeReady, mapTollRow]);

  const handleTollsRefresh = useCallback(() => {
    setTollsRefreshKey((key) => key + 1);
  }, []);

  const handleTollsLoadMore = useCallback(() => {
    if (isLoadingMore) return;
    if (tolls.length >= tollsTotalRecords) return;
    const nextPage = tollsPage + 1;
    setIsLoadingMore(true);
    getTolls(requestFilters, nextPage, TOLLS_PAGE_SIZE)
      .then((result) => {
        setTolls((prev) => [...prev, ...result.content.map(mapTollRow)]);
        setTollsTotalRecords(result.totalElements ?? tollsTotalRecords);
        setTollsPage(nextPage);
      })
      .finally(() => setIsLoadingMore(false));
  }, [isLoadingMore, tolls.length, tollsTotalRecords, tollsPage, requestFilters, mapTollRow]);

  const handleTollsDownload = useCallback(
    (layout?: TableExportLayout) => {
      downloadTollsXlsx(requestFilters, layout).catch(() => {});
    },
    [requestFilters]
  );

  const handleViewDetail = useCallback(async (id: string) => {
    setDetailModal({ open: true, data: null, loading: true });
    try {
      const detail = await getTollDetail(id);
      if (!detail) {
        Notification.error(t("toll.notification.detailError"));
        setDetailModal({ open: false, data: null, loading: false });
        return;
      }
      setDetailModal({ open: true, data: detail, loading: false });
    } catch {
      Notification.error(t("toll.notification.detailError"));
      setDetailModal({ open: false, data: null, loading: false });
    }
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailModal({ open: false, data: null, loading: false });
  }, []);

  let detailBody: React.ReactNode;
  if (detailModal.loading) {
    detailBody = <DetailFormSkeleton rows={8} splitRows={[0]} />;
  } else if (detailModal.data) {
    detailBody = <TollDetailForm detail={detailModal.data} momentLocale={momentLocale} />;
  } else {
    detailBody = <div className="padding-20 text-color-gray">{t("toll.dialog.noData")}</div>;
  }

  return (
    <div className="padding-10 padding-20-sm display-flex flex-column gap-10 gap-20-sm min-height-100pct">
      <MonthNavigation
        endDate={endDate}
        startDate={startDate}
        currentMonth={currentMonth}
        onPrevMonth={goToPrevMonth}
        onNextMonth={goToNextMonth}
        onEndDateChange={(date) => dispatch(setEndDate(date))}
        onStartDateChange={(date) => dispatch(setStartDate(date))}
        showDateRangePicker={showDateRangePicker}
        onClick={() => setShowDateRangePicker(!showDateRangePicker)}
        onCloseDateRangePicker={() => setShowDateRangePicker(false)}
      />
      <SelectedFilters requestFilters={requestFilters} />
      {isLoadingSummary ? (
        <KpiCardsSkeleton
          className="display-grid gap-10 grid-cols-1 grid-cols-2-sm grid-cols-4-lg"
          style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
          contentGapClassName="gap-20"
        />
      ) : (
        <div
          className="display-grid gap-10 grid-cols-1 grid-cols-2-sm grid-cols-4-lg"
          style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
        >
          <TollKpiCard
            label={t("toll.kpi.totalExpense")}
            comparison={toKpiComparison(summary.totalSpent)}
            formatComparisonValue={formatComparisonCurrency}
            tooltip={
              <KpiInfoTooltip
                title={t("toll.kpi.totalExpense")}
                description={t("toll.kpi.totalExpense.tooltip")}
                lines={[`R$ ${formatCurrency(totalExpense)}`]}
                periodRange={kpiPeriodRange}
              />
            }
            value={formatCurrency(totalExpense)}
            icon={<CurrencyIcon />}
            deltaPercentage={totalExpenseDelta}
          />
          <TollKpiCard
            label={t("toll.kpi.totalPasses")}
            comparison={toKpiComparison(summary.totalPassages)}
            formatComparisonValue={formatInteger}
            tooltip={
              <KpiInfoTooltip
                title={t("toll.kpi.totalPasses")}
                description={t("toll.kpi.totalPasses.tooltip")}
                lines={[formatInteger(totalPasses)]}
                periodRange={kpiPeriodRange}
              />
            }
            value={formatInteger(totalPasses)}
            icon={
              <span
                className="text-color-primary"
                style={{ position: "relative", display: "inline-block", width: 28, height: 28 }}
              >
                <span
                  className="rioglyph rioglyph-option-horizontal"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%) scaleX(1.4)",
                    fontSize: 22,
                    zIndex: 1,
                  }}
                />
                <span
                  className="rioglyph rioglyph-sort"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: 22,
                    zIndex: 2,
                  }}
                />
              </span>
            }
            deltaPercentage={totalPassesDelta}
          />
          <TollKpiCard
            label={t("toll.kpi.avgCostPerPass")}
            comparison={toKpiComparison(summary.averageCostPerPassage)}
            formatComparisonValue={formatComparisonCurrency}
            tooltip={
              <KpiInfoTooltip
                title={t("toll.kpi.avgCostPerPass")}
                description={t("toll.kpi.avgCostPerPass.tooltip")}
                lines={[formatAverageFormula(totalExpense, totalPasses, averageCostPerPass)]}
                periodRange={kpiPeriodRange}
              />
            }
            value={formatCurrency(averageCostPerPass)}
            icon={<CurrencyIcon />}
            deltaPercentage={averageCostPerPassDelta}
          />
          <TollKpiCard
            label={t("toll.kpi.avgCostPerVehicle")}
            comparison={toKpiComparison(summary.averageCostPerVehicle)}
            formatComparisonValue={formatComparisonCurrency}
            tooltip={
              <KpiInfoTooltip
                title={t("toll.kpi.avgCostPerVehicle")}
                description={t("toll.kpi.avgCostPerVehicle.tooltip")}
                lines={[formatAverageFormula(totalExpense, totalVehicles, averageCostPerVehicle)]}
                periodRange={kpiPeriodRange}
                placement="bottom-end"
              />
            }
            value={formatCurrency(averageCostPerVehicle)}
            icon={<CurrencyIcon />}
            deltaPercentage={averageCostPerVehicleDelta}
          />
        </div>
      )}

      <div
        className="display-grid gap-10 gap-20-sm grid-cols-1 grid-cols-2-md"
        style={{ gridTemplateColumns: "1fr 1fr" }}
      >
        {isLoadingDailyExpenses ? (
          <DailyFleetExpenseChartSkeleton />
        ) : (
          <DailyFleetExpenseChart data={dailyExpenseData} />
        )}
        {isLoadingTopTollPlazas ? <TopTollPlazasCardSkeleton /> : <TopTollPlazasCard items={topTollPlazas} />}
      </div>

      {isLoadingTopConcessionaires ? (
        <TopConcessionariasChartSkeleton />
      ) : (
        <TopConcessionariasChart data={topConcessionaires} />
      )}

      <TollConcentrationMapCard pins={mapPins} />

      <TollsTable
        data={tolls}
        totalData={tollsTotalRecords}
        onLoadMore={handleTollsLoadMore}
        isLoadingMore={isLoadingMore}
        isLoading={isLoadingTable}
        onRefresh={handleTollsRefresh}
        onDownload={handleTollsDownload}
        onViewDetail={handleViewDetail}
      />

      <Dialog
        show={detailModal.open}
        title={<span className="text-medium text-size-16">{t("toll.dialog.detailTitle")}</span>}
        body={detailBody}
        footer={
          <div className="display-flex flex-wrap gap-10 justify-content-end">
            <button type="button" className="btn btn-default width-100pct-xs" onClick={handleCloseDetail}>
              {t("toll.dialog.close")}
            </button>
          </div>
        }
        onClose={handleCloseDetail}
        showCloseButton
        bsSize="md"
      />
    </div>
  );
};

export default TollsScreen;
